"use client";

import { useEffect } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

/**
 * Root-layout helper. If `?tour=<id>` remains in the URL after initial render
 * settles and no mounted `<Tour>` has registered itself in `claimedIds`,
 * strip the param. Handles malformed deep-links, the `?tour=1` wizard relic,
 * and `?tour=<foo>` arriving at a page that doesn't host `foo`.
 *
 * `claimedIds` is derived from the entire tour catalogue — any known id will
 * be claimed by its page (which strips the param itself on force-launch).
 * Unknown ids hit this fallback.
 */
export function useTourParamCleanup(claimedIds: Set<string>) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const paramValue = searchParams.get("tour");

  useEffect(() => {
    if (!paramValue) return;
    if (claimedIds.has(paramValue)) return;
    let cancelled = false;
    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        const next = new URLSearchParams(searchParams.toString());
        next.delete("tour");
        const qs = next.toString();
        router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
    };
  }, [paramValue, claimedIds, router, searchParams, pathname]);
}
